/**
 * INFRAESTRUTURA AWS: AS REGRAS DA ESCRITA (11/10/2026, medição da AwsReservationsTab e da Ec2MonitoringTab). Funções
 * puras que a tela usa e os testes cobram dos dois lados.
 */
import { fmtDate } from './formatters';
import { nomeDeExibicao } from './nomeDeExibicao';

type Instancia = { instanceId: string; name?: string };
type Cliente = { id: number; name?: string; tradeName?: string; companyName?: string; ec2InstanceId?: string | null };
type Reserva = { instanceType: string; region: string; instanceCount: number; endDate: string; reservationId?: string | null };

export const nomeDaInstancia = (i: Instancia | undefined, id?: string | null): string =>
  i ? (i.name ? `${i.name} (${i.instanceId})` : i.instanceId) : String(id || '');
export const nomeDoCliente = (c: Cliente): string => nomeDeExibicao(c, `cliente ${c.id}`);

/**
 * D1: associar é MOVER. O cliente tem uma instância só (`clients.ec2InstanceId`): associar aqui o tira de onde estava,
 * e o relatório técnico que vai com a fatura passa a ser desta máquina. A tela fazia isso num clique, sem perguntar.
 */
export function confirmacaoDeAssociar(cliente: Cliente, alvo: Instancia, atual?: Instancia) {
  const deOnde = cliente.ec2InstanceId
    ? `${nomeDoCliente(cliente)} está em ${nomeDaInstancia(atual, cliente.ec2InstanceId)}; associar aqui o tira de lá.`
    : `${nomeDoCliente(cliente)} não está em nenhuma instância hoje.`;
  return {
    title: 'Associar o cliente a esta instância?',
    message: deOnde,
    consequence: `O relatório técnico que vai com a fatura de ${nomeDoCliente(cliente)} passa a ser desta máquina (${nomeDaInstancia(alvo)}).`,
    confirmLabel: 'Associar',
  };
}

export function confirmacaoDeRemoverCliente(cliente: Cliente, instancia: Instancia) {
  return {
    title: 'Remover o cliente desta instância?',
    message: `${nomeDoCliente(cliente)} sai de ${nomeDaInstancia(instancia)}.`,
    consequence: 'O relatório técnico desta máquina deixa de sair com a fatura dele.',
    confirmLabel: 'Remover',
    danger: true,
  };
}

/** D2: excluir nomeia a reserva; a que veio da AWS volta no próximo Sincronizar, e sem os vínculos de instância. */
export function confirmacaoDeExcluirReserva(r: Reserva) {
  return {
    title: 'Excluir a reserva?',
    message: `${r.instanceCount}× ${r.instanceType} em ${r.region}, até ${fmtDate(r.endDate)}.`,
    consequence: r.reservationId
      ? 'Veio da AWS: volta no próximo Sincronizar, sem os vínculos de instância que tem hoje.'
      : 'Cadastro manual: sai de vez.',
    confirmLabel: 'Excluir',
    danger: true,
  };
}

/** D3: o Sincronizar regrava todos os campos da reserva que veio da AWS, menos a descrição (aws-sync.service). */
export const FRASE_DA_RESERVA_DA_AWS = 'Veio da AWS: o Sincronizar regrava estes campos. Só a descrição é sua.';

/**
 * D4: silenciar pergunta, e diz o que o código faz. O alerta silenciado só deixa de valer DEPOIS de expirar (o aviso
 * diário de reserva expirada); os de 48 h e 24 h antes do vencimento não leem a marca e continuam chegando.
 */
export function confirmacaoDeSilenciar(r: Reserva) {
  return {
    title: 'Silenciar o alerta desta reserva?',
    message: `${r.instanceCount}× ${r.instanceType} em ${r.region}, vence em ${fmtDate(r.endDate)}.`,
    consequence: 'Depois de expirar, o aviso diário de reserva expirada deixa de chegar. Os avisos de 48 h e 24 h antes do vencimento continuam.',
    confirmLabel: 'Silenciar',
  };
}

export function confirmacaoDeSobDemanda(i: Instancia, ligando: boolean) {
  return ligando
    ? {
        title: 'Marcar como sob demanda?',
        message: nomeDaInstancia(i),
        consequence: 'Ela sai da cobertura e libera (ou deixa de receber) vaga de reserva, no próximo Sincronizar.',
        confirmLabel: 'Marcar',
      }
    : {
        title: 'Voltar a contar para reserva?',
        message: nomeDaInstancia(i),
        consequence: 'Ela volta à cobertura e pode ocupar vaga de reserva no próximo Sincronizar.',
        confirmLabel: 'Voltar',
      };
}

export function confirmacaoDeDesvincular(i: Instancia, r: Reserva) {
  return {
    title: 'Desvincular a instância da reserva?',
    message: `${nomeDaInstancia(i)} sai de ${r.instanceCount}× ${r.instanceType} em ${r.region}.`,
    consequence: 'A vaga fica ociosa e segue cobrada até outra instância ocupá-la.',
    confirmLabel: 'Desvincular',
  };
}

/** D5: as métricas da EC2 valem para UMA instância num período; a chave decide se a resposta ainda é da tela. */
export const chaveDasMetricas = (instancia: string, horas: number) => `${instancia}|${horas}`;
